import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Calendar, MessageCircle, Users, Sparkles, Smartphone, Share2, XCircle } from "lucide-react";

const GoogleSpacesHistory = () => {
  const timelineEvents = [
    {
      year: "May 2016",
      title: "Google Spaces Launched",
      description: "Google launched Spaces as a small group sharing app designed to help friends share content, links, and have focused conversations around specific topics.",
      icon: <Sparkles className="h-6 w-6" />
    },
    {
      year: "May 2016",
      title: "Mobile Apps Released",
      description: "Google released Spaces apps for Android and iOS, positioning it as a mobile-first group messaging experience integrated with Google Search.",
      icon: <Smartphone className="h-6 w-6" />
    },
    {
      year: "Mid 2016",
      title: "Feature Updates",
      description: "Google added various features including improved sharing capabilities, better integration with YouTube and Chrome, and enhanced search within spaces.",
      icon: <Share2 className="h-6 w-6" />
    },
    {
      year: "Late 2016",
      title: "Declining Usage",
      description: "Despite Google's efforts, Spaces struggled to gain traction against established competitors like Slack, Facebook Groups, and even Google's own Hangouts.",
      icon: <Users className="h-6 w-6" />
    },
    {
      year: "March 2017",
      title: "Shutdown Announced",
      description: "Google announced that Spaces would be discontinued, giving users about a month to export their data before the service would be shut down.",
      icon: <Calendar className="h-6 w-6" />
    },
    {
      year: "April 17, 2017",
      title: "Complete Shutdown",
      description: "Google Spaces was completely discontinued after less than a year of operation, becoming one of Google's shortest-lived consumer products.",
      icon: <XCircle className="h-6 w-6" />
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The History of Google Spaces - A Short-Lived Experiment",
    "description": "Explore the brief history of Google Spaces from its launch in May 2016 to its shutdown in April 2017 - one of Google's shortest-lived consumer products.",
    "url": "https://cramstudywith.me/google-spaces/history/",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "author": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>The History of Google Spaces - A Short-Lived Experiment | CramStudyWith.me</title>
        <meta name="description" content="Explore the brief history of Google Spaces from its launch in May 2016 to its shutdown in April 2017 - one of Google's shortest-lived consumer products." />
        <meta name="keywords" content="Google Spaces history, Google Spaces shutdown, Google Spaces timeline, Google messaging apps, Google graveyard" />
        <link rel="canonical" href="https://cramstudywith.me/google-spaces/history/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              The History of Google Spaces
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              From launch to shutdown in under a year - explore the brief but instructive story of Google's failed group messaging experiment.
            </p>
            <Link to="/google-spaces/">
              <Button size="lg" variant="outline" className="gap-2">
                <ArrowRight className="h-5 w-5" />
                Find a Google Spaces Alternative
              </Button>
            </Link>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Google Spaces Timeline</h2>
              <div className="space-y-8">
                {timelineEvents.map((event, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-secondary" />
                    <CardHeader className="flex flex-row items-center gap-4">
                      <div className="p-3 rounded-full bg-primary/10 text-primary">
                        {event.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-primary">{event.year}</div>
                        <CardTitle className="text-xl">{event.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{event.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why It Failed Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">Why Google Spaces Failed</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Crowded Market
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Spaces entered a market already dominated by Slack, Facebook Groups, WhatsApp, and even Google's own Hangouts, leaving little room for differentiation.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-primary" />
                      Unclear Value Proposition
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Users struggled to understand why they needed Spaces when they already had other Google apps and established messaging platforms for group communication.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-primary" />
                      Limited Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Spaces lacked advanced features that competitors offered, making it hard to convince users to switch from their existing communication tools.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Google's Messaging Strategy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Google's fragmented messaging strategy with multiple apps (Hangouts, Allo, Duo, Spaces) confused users and diluted focus across products.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Legacy Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">What We Can Learn from Google Spaces</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Google Spaces serves as a reminder that even tech giants can struggle with product launches in competitive markets. The app's failure highlighted the importance of clear differentiation and avoiding market fragmentation.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                For users seeking focused group collaboration tools today, especially for studying and learning, <Link to="/google-spaces/" className="text-primary hover:underline">CramStudyWith.me offers purpose-built alternatives</Link> with video collaboration, productivity tools, and features designed specifically for educational success.
              </p>
              <Link to="/google-spaces/">
                <Button size="lg" className="gap-2">
                  Explore Google Spaces Alternatives
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GoogleSpacesHistory;